/** @typedef {'logo' | 'background'} BrandingAssetType */

export const BRANDING_ASSET_TYPES = {
  LOGO: 'logo',
  BACKGROUND: 'background',
};

/** Recommended output specs shown in the UI. */
export const LOGO_SPECS = {
  width: 512,
  height: 512,
  aspectRatio: '1:1',
  mimeType: 'image/png',
  label: '512×512 PNG with transparent background',
};

export const BACKGROUND_SPECS = {
  width: 1920,
  height: 1080,
  aspectRatio: '16:9',
  mimeType: 'image/png',
  label: '1920×1080 (16:9) — scales with cover on mobile and desktop',
};

export const LOGO_ACCEPT = 'image/png,image/webp,image/svg+xml,image/jpeg';
export const BACKGROUND_ACCEPT =
  'image/png,image/jpeg,image/webp,image/gif,image/avif';

/**
 * @param {string} companyName
 * @param {string} brandColor
 */
function fillPrompt(template, companyName, brandColor) {
  return template
    .replace(/\{companyName\}/g, companyName || 'the company')
    .replace(/\{brandColor\}/g, brandColor || '#D85A30');
}

/** @type {{ id: string, label: string, description: string, template: string }[]} */
export const DEFAULT_LOGO_PROMPTS = [
  {
    id: 'transparent-minimal',
    label: 'Transparent icon mark',
    description: 'Simple flat icon on a clear background for the sidebar.',
    template:
      'Create a clean, modern logo icon for {companyName}. Flat vector style, ' +
      'single recognizable symbol, fully transparent background, no text, centered, ' +
      'high contrast, optimized for a 512×512 PNG sidebar logo.',
  },
  {
    id: 'transparent-wordmark',
    label: 'Transparent wordmark',
    description: 'Company name as a crisp horizontal mark.',
    template:
      'Design a professional horizontal wordmark for {companyName} using accent color ' +
      '{brandColor}. Transparent background, crisp edges, SaaS-quality branding, ' +
      '512×512 canvas with generous padding, no mockup frame.',
  },
  {
    id: 'transparent-badge',
    label: 'Transparent badge',
    description: 'Rounded badge with initial or short mark.',
    template:
      'Create a rounded badge logo for {companyName} with a bold initial or minimal ' +
      'symbol. Transparent PNG, works on light and dark UI, 512×512, no drop shadow ' +
      'outside the artwork.',
  },
];

/** @type {{ id: string, label: string, description: string, template: string }[]} */
export const DEFAULT_BACKGROUND_PROMPTS = [
  {
    id: 'subtle-brand-mesh',
    label: 'Subtle brand mesh',
    description: 'Soft gradient mesh tuned to your brand color.',
    template:
      'Abstract soft gradient mesh wallpaper for a business operations dashboard. ' +
      'Colors inspired by {brandColor}. No text, no logos, calm and professional, ' +
      '16:9 aspect ratio, keep the center calm for mobile crop, subtle depth only.',
  },
  {
    id: 'dark-ambient',
    label: 'Dark ambient glow',
    description: 'Cinematic dark backdrop with soft glow.',
    template:
      'Dark ambient dashboard wallpaper with soft glowing gradients and subtle grain. ' +
      'Accent color {brandColor}, no text, 16:9, low contrast behind white UI cards, ' +
      'safe center area for mobile.',
  },
  {
    id: 'light-geometric',
    label: 'Light geometric',
    description: 'Airy pattern that stays readable behind content.',
    template:
      'Very subtle light geometric pattern background for a SaaS dashboard. Off-white ' +
      'base with gentle {brandColor} accents, 16:9, extremely low contrast, no text, ' +
      'designed to sit behind white content panels on desktop and mobile.',
  },
];

/**
 * @param {BrandingAssetType} assetType
 * @param {string} presetId
 * @param {string} customPrompt
 * @param {{ companyName?: string, brandColor?: string }} ctx
 */
export function resolveBrandingPrompt(assetType, presetId, customPrompt, ctx = {}) {
  const trimmed = typeof customPrompt === 'string' ? customPrompt.trim() : '';
  if (trimmed) return trimmed;

  const presets =
    assetType === BRANDING_ASSET_TYPES.LOGO ?
      DEFAULT_LOGO_PROMPTS :
      DEFAULT_BACKGROUND_PROMPTS;
  const preset = presets.find((p) => p.id === presetId) || presets[0];
  return fillPrompt(preset.template, ctx.companyName, ctx.brandColor);
}

/**
 * @param {string} base64
 * @param {string} mimeType
 * @returns {Blob}
 */
export function base64ToBlob(base64, mimeType) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType || 'image/png' });
}

/**
 * @param {File} file
 * @returns {string}
 */
export function filePreviewUrl(file) {
  return URL.createObjectURL(file);
}

/**
 * @param {string} mimeType
 * @returns {boolean}
 */
export function isAnimatedBackgroundMime(mimeType) {
  return mimeType === 'image/gif' || mimeType === 'image/webp';
}
